import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LocalizedEditService } from '../../../transloco/localized-edit.service';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'projects/shared/modal/modal.service';
import { AssetsStep } from '../../../models/wizard/assets-step.model';
import { AssetsStepConfig } from '../../../models/wizard/config/assets-step-config.model';
import { Asset } from '../../../models/asset.model';
import { AssetDialogComponent, BulkSaveTransaction } from '../asset-dialog/asset-dialog.component';
import { MortgageApplication } from '../../../models/mortgage-app.model';
import { AssetListComponent } from '../asset-list/asset-list.component';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { WizardStepComponentBase } from '../wizard-step-base.component';
import * as _ from 'lodash';
import { ErrorMessage } from '../../../models/error-message.model';
import { MortgageApplicationService } from '../../../services/mortgage-application.service';
import { Observer } from 'rxjs';
import { getErrorMessageOrDefault } from 'projects/shared/utils/error.utils';
import { activeOnly } from '../../../utils/entity-state';

@Component({
    selector: 'assets-step',
    templateUrl: 'assets-step.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetsStepComponent extends WizardStepComponentBase<AssetsStep, AssetsStepConfig> implements OnInit, OnDestroy {

  @ViewChild(AssetListComponent) assetListComponent: AssetListComponent | undefined;

  private modalOptions: NgbModalOptions = {
    size: "lg",
    backdrop: "static",
    container: "#assets-main"
  };

  private modalOptionsBulk: NgbModalOptions = {
    size: "xl",
    backdrop: "static",
    container: "#assets-main"
  };

  private _subscriptionToAssetSavedForCreate: any;

  mortgage: MortgageApplication;

  constructor(
    private readonly _modalService: ModalService,
    private readonly _mortgageService: MortgageApplicationService,
    private readonly _injector: Injector,
    private readonly _cdr: ChangeDetectorRef,
    public readonly i18n: LocalizedEditService
  ) {
    super(_injector);
    this.saveMortgageApplicationBeforeNextStep = true;
    this.mortgage = this.mortgageApplication;
  }

  ngOnInit(): void {
    super.ngOnInit();
    const applicationId = this.mortgage.applicationId;

    const observer: Observer<MortgageApplication> = {
      next: (mortgage: MortgageApplication) => {
        this.mortgage = mortgage;
        this.wizardFlowService.updateContext(mortgage);
        this.stopSpinner();
        this._cdr.detectChanges();
      },
      error: (err: any) => {
        const errorMessage = getErrorMessageOrDefault(err, {
          defaultMessage: this.translocoService.translate('wizard.assets.errorGettingAssets'),
        });
        this.showError(new ErrorMessage(this.translocoService.translate('wizard.assets.errorGettingAssets'), errorMessage));
        this.stopSpinner();
        this._cdr.detectChanges();
      },
      complete: () => {
      }
    }
    this.startSpinner();
    this._mortgageService.getMortgage(applicationId, true).subscribe(observer);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._subscriptionToAssetSavedForCreate) {
      this._subscriptionToAssetSavedForCreate.unsubscribe();
    }
  }

  onAddAssetClicked = () => {
    if (!this.inEditMode) {
      this.showAssetPopup();
    }
  }

  /**
   * Opens the dialog directly in bulk-account mode with no pre-locked
   * institution — the user picks the bank inside the dialog and enters
   * multiple accounts in one trip. Backs the "Add Account" CTA that appears
   * when admin-side `enableAccountGroupingByInstitution` is on.
   */
  onAddAccountClicked = () => {
    if (this.inEditMode) return;
    this.showAssetPopup(undefined, undefined, true);
  }

  onAddAtInstitution = (depository: string) => {
    if (this.inEditMode) {
      return;
    }
    this.showAssetPopup(undefined, depository);
  }

  protected get activeAssetCount(): number {
    return activeOnly(this.mortgage?.assets).length;
  }

  onNextClicked() {
    if (this.step.dontLetUserSkipWithoutAssets && !activeOnly(this.mortgage.assets).length) {
      const errorMessage = new ErrorMessage(
        this.translocoService.translate('wizard.assets.addAssetsErrorTitle'),
        this.translocoService.translate('wizard.assets.addAssetsErrorMessage')
      );
      this.showError(errorMessage);
      return;
    }
    if (this.assetListComponent?.isValid()) {
      super.onNextClicked();
    }
  }

  private showAssetPopup(asset?: Asset, lockedInstitution?: string, startInBulkMode: boolean = false) {
    const isBulk = !!lockedInstitution || startInBulkMode;
    const modal = this._modalService.show(AssetDialogComponent, isBulk ? this.modalOptionsBulk : this.modalOptions);
    if (asset) {
      modal.componentInstance.asset = asset;
    } else {
      modal.componentInstance.selectedBorrowers = [new EnumerationItem(this.currentBorrower.firstName,
        <string>this.currentBorrower.borrowerId?.toString())];
    }
    modal.componentInstance.fieldsToConfig = this.step.fieldConfig;
    modal.componentInstance.inCoApplyFlow = this.wizardFlowService.inCoApplyFlow;
    modal.componentInstance.existingInstitutions = this.getExistingInstitutions(asset);
    if (lockedInstitution) {
      modal.componentInstance.lockedInstitution = lockedInstitution;
      modal.componentInstance.seedAssets = this.getAssetsAtInstitution(lockedInstitution);
    }
    if (startInBulkMode) {
      modal.componentInstance.startInBulkMode = true;
    }
    this._subscriptionToAssetSavedForCreate = modal.componentInstance.saveClickedEmitterForCreate
      .subscribe((e: Asset) => this.onSaveClickedOnAssetModalForCreate(e));
    modal.componentInstance.bulkSavedEmitter
      .subscribe((tx: BulkSaveTransaction) => this.applyBulkSaveTransaction(tx));
  }

  /**
   * Reconciles the dialog's local row list against `mortgage.assets` in a
   * single pass — deletes first to free up assetIds, then upserts. The
   * dialog only emits this on Ok; Cancel skips the emit entirely so the
   * user's in-dialog edits/adds/deletes have zero side effects on the
   * main list until they confirm.
   */
  private applyBulkSaveTransaction = (tx: BulkSaveTransaction): void => {
    const assets = this.mortgage.assets;
    if (!assets) return;

    for (const assetId of tx.removeAssetIds) {
      const idx = assets.findIndex(a => a.assetId === assetId);
      if (idx === -1) continue;
      const target = assets[idx];
      if (target.assetId! < 0) {
        // Never persisted — drop it from the array entirely.
        assets.splice(idx, 1);
      } else {
        // Persisted — backend needs the soft-delete marker.
        target.entityState = 'Deleted';
        target.requireEntityStateForChanges = true;
      }
    }

    for (const asset of tx.upsert) {
      const existing = assets.find(a => a.assetId === asset.assetId && a.entityState !== 'Deleted');
      if (existing) {
        asset.requireEntityStateForChanges = true;
        asset.entityState = 'Modified';
        assets[assets.indexOf(existing)] = asset;
      } else {
        asset.requireEntityStateForChanges = true;
        asset.entityState = 'Added';
        assets.push(asset);
      }
    }

    this.alert = null;
    this.assetListComponent?.refresh();
    this._cdr.markForCheck();
  }

  /**
   * Returns every non-deleted asset whose depository matches the given
   * institution name (case-insensitive). Used to seed the bulk dialog's row
   * table so it shows the full set of accounts at the bank.
   */
  private getAssetsAtInstitution(depository: string): Asset[] {
    const needle = (depository ?? '').trim().toLowerCase();
    if (!needle) return [];
    return (this.mortgage.assets ?? []).filter(a =>
      a.entityState !== 'Deleted' &&
      (a.depository ?? '').trim().toLowerCase() === needle
    );
  }

  private getExistingInstitutions(currentAsset?: Asset): string[] {
    const assets = this.mortgage.assets ?? [];
    const set = new Set<string>();
    assets.forEach(a => {
      if (a === currentAsset) return;
      if (a.entityState === 'Deleted') return;
      const value = (a.depository ?? '').trim();
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  private onSaveClickedOnAssetModalForCreate = (asset: Asset): void => {
    asset.requireEntityStateForChanges = true;
    asset.entityState = 'Added';
    this.mortgage.assets?.push(asset);
    this.alert = null;
    // Re-render in place — DO NOT re-assign `assets` with a filtered copy here.
    // The list's `_assets` is bound to `mortgage.assets` (same reference) so
    // its trash-splice and entityState mutations propagate back to the parent.
    // Re-assigning a filtered copy would diverge those references and cause
    // deleted negative-id assets to resurrect on the next create cycle.
    this.assetListComponent?.refresh();
    this._cdr.markForCheck();
  }
}
